import { useCallback } from "react";
import { createShipment, bookPorter, deleteShipment } from "../shipmentService";
import { getCurrentUTCTimestamp } from "../../../utils/dateResolvers";
import services from "../../../services/orderServices";
import { DeliveryModes, DispatchMode } from "../../../types";

interface CreateShipmentOptions {
  orderNumbers: string[];
  orderIds: Record<string, string>;
  deliveryMode?: DeliveryModes;
  dispatchMode?: DispatchMode;
  userName?: string;
  skipDispatchTransition?: boolean;
  skipGeocodeValidation?: boolean;
}

interface ShipmentCreationReturn {
  createShipmentForOrders: (options: CreateShipmentOptions) => Promise<any>;
  bookPorterForShipment: (shipmentId: string) => Promise<any>;
  deleteShipmentById: (shipmentId: string) => Promise<void>;
  createShipmentAndBookPorter: (
    orderNumbers: string[],
    orderIds: Record<string, string>,
    deliveryMode?: DeliveryModes,
    dispatchMode?: DispatchMode,
    maskDeliveryContactDetails?: boolean,
    userName?: string
  ) => Promise<{ shipment: any; porter: any }>;
}

const transitionOrdersToDispatched = async (
  orderNumbers: string[],
  orderIdMap: Record<string, string>
) => {
  try {
    const transitionPromises = orderNumbers.map(async (orderNo) => {
      try {
        const orderId = orderIdMap[orderNo];
        if (!orderId) {
          throw new Error(`Order ID not found for ${orderNo}`);
        }
        await services.transitionOrderStatus(orderId, "DISPATCH");

      } catch (error) {
        console.error(`✗ Failed to transition order ${orderNo}:`, error);
        throw error;
      }
    });

    await Promise.all(transitionPromises);

  } catch (error) {
    console.error("Failed to transition orders to DISPATCHED:", error);
    throw new Error(
      "Failed to update order status to DISPATCHED. Please try again."
    );
  }
};

const updateOrderDispatchTime = async (orderNumbers: string[]) => {
  const dispatchTime = getCurrentUTCTimestamp();
  try {
    const updatePromises = orderNumbers.map(async (orderNo) => {
      try {
        await services.updateOrderMetaInfo(orderNo, {
          dispatchTime: dispatchTime,
        });
      } catch (error) {
        console.error(
          `Failed to update dispatch time for order ${orderNo}:`,
          error
        );
      }
    });

    await Promise.allSettled(updatePromises);
    console.log(`Updated dispatch time for ${orderNumbers.length} orders`);
  } catch (error) {
    console.error("Failed to update order dispatch times:", error);
  }
};

export const useShipmentCreation = (): ShipmentCreationReturn => {
  const createShipmentForOrders = useCallback(
    async ({
      orderNumbers,
      orderIds,
      deliveryMode,
      dispatchMode,
      userName,
      skipDispatchTransition = false,
      skipGeocodeValidation,
    }: CreateShipmentOptions) => {
      try {
        if (!skipDispatchTransition) {
          await transitionOrdersToDispatched(orderNumbers, orderIds);
          await updateOrderDispatchTime(orderNumbers);
        }

        const shipmentPayload = {
          orderNumbers,
          additionalComments: userName || "",
          deliveryMode,
          dispatchMode,
        };

        const response = await createShipment(
          shipmentPayload,
          skipGeocodeValidation
        );

        if (!response.success) {
          throw new Error(response.message || "Failed to create shipment");
        }

        return response.data;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.errorMsg ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to create shipment. Please try again.";

        throw new Error(errorMessage);
      }
    },
    []
  );

  const bookPorterForShipment = useCallback(
    async (shipmentId: string, maskDeliveryContactDetails?: boolean) => {
      try {
        const porterBookingPayload = {
          shipmentId,
          maskDeliveryContactDetails,
        };

        const response = await bookPorter(porterBookingPayload);

        if (!response.request_id || !response.order_id) {
          throw new Error("Failed to book porter - unexpected response format");
        }

        return response;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.errorMsg ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to book porter";

        throw new Error(errorMessage);
      }
    },
    []
  );

  const deleteShipmentById = useCallback(async (shipmentId: string) => {
    try {
      await deleteShipment(shipmentId);
    } catch (error) {
      console.error("Failed to delete shipment:", error);
    }
  }, []);

  const createShipmentAndBookPorter = useCallback(
    async (
      orderNumbers: string[],
      orderIds: Record<string, string>,
      deliveryMode?: DeliveryModes,
      dispatchMode?: DispatchMode,
      maskDeliveryContactDetails?: boolean,
      userName?: string
    ) => {
      let shipmentData: any = null;

      try {
        shipmentData = await createShipmentForOrders({
          orderNumbers,
          orderIds,
          deliveryMode,
          dispatchMode,
          userName,
          skipDispatchTransition: false,
        });
        const porterResponse = await bookPorterForShipment(
          shipmentData.shipmentId,
          maskDeliveryContactDetails
        );
        return {
          shipment: shipmentData,
          porter: porterResponse,
        } as const;
      } catch (error) {
        if (shipmentData?.shipmentId) {
          await deleteShipmentById(shipmentData.shipmentId);
        }
        throw error;
      }
    },
    [createShipmentForOrders, bookPorterForShipment, deleteShipmentById]
  );

  return {
    createShipmentForOrders,
    bookPorterForShipment,
    deleteShipmentById,
    createShipmentAndBookPorter,
  };
};
